import { createContext, useContext } from 'react';
import type { EnforceDisabledReason } from '@kirin/api-client/contracts/policies';

export interface EnforceAvailability {
  /** Mirrors the API's `policy.supportsEnforce`. */
  supportsEnforce: boolean;
  /**
   * Mirrors the API's `policy.enforceDisabledReason`, so a nested selector
   * explains WHY Enforce is unavailable in the same words as the parent one —
   * "Contact Support to enable Enforce" for a flagged-off org versus the
   * default "Coming Soon" for a policy that cannot enforce by design.
   */
  enforceDisabledReason?: EnforceDisabledReason;
}

/**
 * Whether Enforce may be selected anywhere inside the current settings form.
 *
 * Mirrors the API's `policy.supportsEnforce`, which the backend already folds
 * the org's `enforce_policy_mode` flag into. Nested mode-valued sub-policies
 * (KIR-4426's Real-Time Tool-Call Analysis) render through
 * `PolicySettingField`, which has ~14 call sites in `PolicySettingsForm` and
 * no access to the policy object — a context keeps the gate in one place
 * instead of threading a prop through all of them.
 *
 * Defaults to enforce-allowed so a form that never provides it (or a field
 * rendered in isolation, e.g. a test) behaves exactly as before.
 */
export const EnforceAvailabilityContext = createContext<EnforceAvailability>({
  supportsEnforce: true,
});

export function useEnforceAvailability(): EnforceAvailability {
  return useContext(EnforceAvailabilityContext);
}
